import { Inject, Injectable, OnDestroy } from "@angular/core";
import { Permissions, PermissionTypes } from "../enums/permissions.enum";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { NbAccessChecker } from "@nebular/security";
import { map, startWith, switchMap, take, takeUntil } from "rxjs/operators";
import {
  API_BASE_URL,
  AssetDocumentViewModel,
  HelixDocumentClient,
} from "@helix/client";

export enum DocumentApiRoutesEnum {
  DocumentCreate = "DocumentCreate",
}

@Injectable({ providedIn: "root" })
export class DocumentService implements OnDestroy {
  private destroyed$ = new Subject<void>();
  private refreshTrigger$ = new Subject<void>();
  private readonly permissions$: {
    [key: string]: BehaviorSubject<boolean>;
  } = {};
  fileIcons: { [extension: string]: string } = {
    pdf: "assets/icons/doc.png",
    default: "assets/icons/file-regular.svg",
  };

  private permissionTypeResourceMapping: Map<PermissionTypes, Permissions> =
    new Map([
      [PermissionTypes.Create, Permissions.documentCreate],
      [PermissionTypes.Delete, Permissions.documentDelete],
      [PermissionTypes.Execute, Permissions.documentExecute],
      [PermissionTypes.Read, Permissions.documentRead],
      [PermissionTypes.Update, Permissions.documentUpdate],
      [PermissionTypes.View, Permissions.documentView],
    ]);

  constructor(
    private readonly accessChecker: NbAccessChecker,
    private readonly documentClient: HelixDocumentClient,
    @Inject(API_BASE_URL) private readonly baseUrl: string,
  ) {
    this.createPermissionsDictionary();
  }

  public getEndpointUrl(route: DocumentApiRoutesEnum): string {
    if (route === DocumentApiRoutesEnum.DocumentCreate) {
      return this.baseUrl + "/api/Document/CreateAssetDocument";
    } else {
      throw new Error("Document API route not configured properly.");
    }
  }
  private getAssetDocuments(
    assetId: number,
  ): Observable<AssetDocumentViewModel[]> {
    return this.refreshTrigger$.pipe(
      startWith(undefined),
      switchMap(() =>
        this.documentClient
          .getAssetDocumentsByAssetId(assetId)
          .pipe(takeUntil(this.destroyed$)),
      ),
      takeUntil(this.destroyed$),
    );
  }
  getAssetDocumentsWithIcons(
    assetId: number,
  ): Observable<AssetDocumentViewModelWithIcons[]> {
    const self = this;
    return this.getAssetDocuments(assetId).pipe(
      map((x) => x.map((row) => ({ ...row, icon: self.getFileIcon(row) }))),
      takeUntil(this.destroyed$),
    );
  }
  getAssetDocumentUrl(document: AssetDocumentViewModel): Observable<string> {
    return this.documentClient
      .getDocumentUri(document.documentId)
      .pipe(take(1), takeUntil(this.destroyed$));
  }

  refreshAssetDocuments() {
    this.refreshTrigger$.next();
  }

  public getFileIcon(document: AssetDocumentViewModel): string {
    const fileExtension = this.getFileExtension(document.savedFileName);

    return (
      this.fileIcons[fileExtension.toLowerCase()] || this.fileIcons.default
    );
  }

  getFileExtension(filename: string): string {
    const index = filename.lastIndexOf(".");

    if (index > 0) {
      return filename.substring(index + 1);
    }

    return "";
  }

  public getDocumentPermissions(permissionType: PermissionTypes) {
    const permissionSubject = this.permissions$[permissionType];
    if (permissionSubject.value === undefined) {
      this.setPermissionsSubject(permissionType, permissionSubject);
    }
    return permissionSubject;
  }
  private createPermissionsDictionary() {
    const permissionValues = Object.values(PermissionTypes);
    permissionValues.forEach((value) => {
      this.permissions$[value] = new BehaviorSubject<boolean>(undefined);
    });
  }

  private setPermissionsSubject(
    permission: PermissionTypes,
    permissionSubject: BehaviorSubject<boolean>,
  ): void {
    const resource = this.permissionTypeResourceMapping.get(permission);
    this.accessChecker
      .isGranted(permission, resource)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((access) => {
        permissionSubject.next(access);
      });
  }
  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}

export interface AssetDocumentViewModelWithIcons extends AssetDocumentViewModel {
  icon?: string;
}
